/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import { Button, Dropdown, Flex, Modal, notification } from 'antd';

import {
  AntdRouterLink,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteSpecialOffer } from 'services';
import type { SpecialOfferSummary } from 'types';
import {
  adjustPageNumber,
  dateTimeFormat,
  generateTableDataSource,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    SPECIAL_OFFERS: { UPDATE_URL: SPECIAL_OFFERS_UPDATE_URL },
  },
} = PATHNAME;

type SpecialOffersTableProps = {
  loading: boolean;
  total: number;
  specialOffers: SpecialOfferSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const SpecialOffersTable = React.memo<SpecialOffersTableProps>(
  ({
    loading,
    total,
    specialOffers,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteSpecialOffer = useLockFn(
      async ({ id }: SpecialOfferSummary) => {
        deleteSpecialOffer({ id })
          .then(() => {
            handleRefreshData();
            notification.success({
              message: 'تخفیف ویژه مورد نظر با موفقیت حذف شد.',
            });
          })
          .catch(message => {
            notification.error({
              message,
            });
          });
      },
    );

    const specialOffersDataSource = generateTableDataSource(
      specialOffers,
      'id',
    );

    return (
      <PrimitiveTable
        columns={[
          {
            title: 'عنوان',
            dataIndex: 'title',
            key: 'title',
            width: '30%',
            ellipsis: true,
            render: (_, { id, title }) => (
              <AntdRouterLink
                ellipsis
                to={`${SPECIAL_OFFERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {title}
              </AntdRouterLink>
            ),
          },
          {
            title: 'مقدار',
            dataIndex: 'percent',
            key: 'percent',
            ellipsis: true,
            render: (_, { percent }) => <span>%{percent}</span>,
          },
          {
            title: 'تاریخ شروع',
            dataIndex: 'startTime',
            key: 'startTime',
            width: 170,
            ellipsis: true,
            render: (_, { duration: { startTime } }) => (
              <bdi>{dateTimeFormat(new Date(startTime))}</bdi>
            ),
          },
          {
            title: 'تاریخ پایان',
            dataIndex: 'endTime',
            key: 'endTime',
            width: 170,
            ellipsis: true,
            render: (_, { duration: { endTime } }) => (
              <bdi>{dateTimeFormat(new Date(endTime))}</bdi>
            ),
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-special-offer',
                          label: (
                            <AntdRouterLink
                              to={`${SPECIAL_OFFERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش تخفیف ویژه
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-special-offer',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف تخفیف ویژه مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteSpecialOffer(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف تخفیف ویژه</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={specialOffersDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
SpecialOffersTable.displayName = 'SpecialOffersTable';

export { SpecialOffersTable };
